export interface AddOnOptionDef {
  name: string;
  values: string[];
}

export interface AddOnOptionVariant {
  id: string;
  sku?: string;
  title: string;
  selectedOptions?: { name: string; value: string }[];
}

/** True when every variant carries selectedOptions (otherwise callers fall back to the legacy select). */
export function variantsHaveOptions(variants: AddOnOptionVariant[]): boolean {
  return variants.length > 0 && variants.every((v) => Array.isArray(v.selectedOptions) && v.selectedOptions.length > 0);
}

export function optionValueOf(variant: AddOnOptionVariant | undefined, optionName: string): string | undefined {
  return variant?.selectedOptions?.find((o) => o.name === optionName)?.value;
}

/** Values of `optionName` that exist on at least one variant, in Shopify's declared order. */
export function availableOptionValues(
  option: AddOnOptionDef,
  variants: AddOnOptionVariant[],
): string[] {
  const present = new Set(
    variants.map((v) => optionValueOf(v, option.name)).filter((x): x is string => !!x),
  );
  const ordered = option.values.filter((val) => present.has(val));
  // Include any value present on variants but missing from the declared list.
  present.forEach((val) => {
    if (!ordered.includes(val)) ordered.push(val);
  });
  return ordered;
}

/**
 * Pick the variant after the user changed one option. Prefers an exact match on all
 * currently chosen values; otherwise the first variant matching the changed option.
 */
export function pickVariantForOptionChange(
  variants: AddOnOptionVariant[],
  currentVariantId: string,
  optionName: string,
  newValue: string,
): AddOnOptionVariant | undefined {
  const current = variants.find((v) => v.id === currentVariantId);
  const wanted = new Map<string, string>();
  (current?.selectedOptions ?? []).forEach((o) => wanted.set(o.name, o.value));
  wanted.set(optionName, newValue);
  const exact = variants.find((v) =>
    (v.selectedOptions ?? []).every((o) => wanted.get(o.name) === o.value),
  );
  if (exact) return exact;
  return variants.find((v) => optionValueOf(v, optionName) === newValue);
}

export const ZAMST_HEADING = 'ZAMST braces';

export const isZamst = (a: { vendor?: string | null }): boolean =>
  (a.vendor ?? '').trim().toLowerCase() === 'zamst';

/** Split add-ons into the regular list (order unchanged) and ZAMST braces sorted by title. */
export function splitZamst<T extends { vendor?: string | null }>(
  addons: T[],
  getTitle: (a: T) => string,
): { regular: T[]; zamst: T[] } {
  return {
    regular: addons.filter((a) => !isZamst(a)),
    zamst: addons.filter(isZamst).sort((a, b) => getTitle(a).localeCompare(getTitle(b))),
  };
}
